import { useState, type FormEvent } from 'react';
import { Check, TriangleAlert } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { ErrorCode } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { ApiError, errorMessage } from '../../../lib/api';
import { portalLogin, portalPasswordForgot, usePortalOrganization } from '../api';
import { PasswordInput } from '../activation/PasswordScreen';
import { PhoneFrame } from '../frame/PhoneFrame';
import styles from '../frame/Phone.module.css';
import { readLastEmail, storeLastEmail } from '../magic';
import type { PortalCodeChallenge, PortalLoginSession } from '../types';
import { CodeStep } from './CodeStep';
import local from './Login.module.css';

interface LoginError {
  title: string;
  warning: string | null;
}

/**
 * `/portal/:orgSlug/login` — Mandantenportal 11 (login), 12 (code on an unknown device, `CodeStep`), 13 (wrong
 * password: danger banner + the remaining attempts), the inline „Passwort vergessen" form (own).
 */
export function LoginPage() {
  const { orgSlug = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const organization = usePortalOrganization(orgSlug);
  const [email, setEmail] = useState(() => readLastEmail(orgSlug));
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<LoginError | null>(null);
  const [challenge, setChallenge] = useState<PortalCodeChallenge | null>(null);
  const [mode, setMode] = useState<'login' | 'forgot' | 'forgotSent'>('login');

  function enterPortal(session: PortalLoginSession) {
    queryClient.removeQueries({ queryKey: ['portal'] });
    void navigate(session.portalHref, { replace: true });
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError(null);
    const address = email.trim();
    storeLastEmail(orgSlug, address);
    try {
      const outcome = await portalLogin(orgSlug, { email: address, password, remember });
      if ('codeRequired' in outcome) setChallenge(outcome);
      else enterPortal(outcome);
    } catch (cause) {
      if (cause instanceof ApiError && cause.code === ErrorCode.UNAUTHENTICATED) {
        setError({ title: cause.message, warning: cause.detailString('warning') });
      } else if (cause instanceof ApiError && Object.keys(cause.fields).length > 0) {
        setError({ title: Object.values(cause.fields)[0] ?? cause.message, warning: null });
      } else {
        setError({ title: errorMessage(cause), warning: null });
      }
    } finally {
      setPending(false);
    }
  }

  async function requestReset(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      await portalPasswordForgot(orgSlug, email.trim());
      setMode('forgotSent');
    } catch (cause) {
      setError({ title: errorMessage(cause), warning: null });
    } finally {
      setPending(false);
    }
  }

  const org = organization.data;
  const contact = org ? (org.phone ?? org.email) : null;
  const footer =
    org && mode === 'login' && !error ? (
      <div className={local.help}>
        Fragen zum Zugang? {org.name}
        {contact ? ` · ${contact}` : ''}
      </div>
    ) : null;

  if (organization.isError) {
    const notFound = organization.error instanceof ApiError && organization.error.status === 404;
    return (
      <PhoneFrame>
        <div className={local.head}>
          <div className={styles.brand}>DAMOKLES</div>
          <div className={styles.brandSub}>Mandantenportal</div>
          <h1 className={clsx(styles.title, local.headTitle)}>
            {notFound ? 'Dieses Portal gibt es nicht.' : errorMessage(organization.error)}
          </h1>
          <p className={styles.text}>
            {notFound
              ? 'Bitte prüfen Sie die Adresse aus Ihrer E-Mail.'
              : 'Bitte versuchen Sie es in einem Moment erneut.'}
          </p>
        </div>
      </PhoneFrame>
    );
  }

  if (challenge) {
    return (
      <PhoneFrame>
        <CodeStep
          slug={orgSlug}
          challenge={challenge}
          onChallenge={setChallenge}
          onSession={enterPortal}
          onRestart={() => {
            setChallenge(null);
            setPassword('');
          }}
        />
      </PhoneFrame>
    );
  }

  if (mode !== 'login') {
    return (
      <PhoneFrame>
        <form className={local.form} onSubmit={(event) => void requestReset(event)} noValidate>
          <div className={local.head}>
            <div className={styles.brand}>DAMOKLES</div>
            <div className={styles.brandSub}>Mandantenportal</div>
            <h1 className={clsx(styles.title, local.headTitle)}>Passwort zurücksetzen</h1>
            <p className={styles.text}>
              Wir schicken Ihnen einen Link, mit dem Sie ein neues Passwort festlegen.
            </p>
          </div>
          <div className={`${styles.fields} ${local.fields}`}>
            {mode === 'forgotSent' ? (
              <div className={styles.notice} role="status">
                Falls ein Zugang besteht, ist ein Link zum Zurücksetzen unterwegs.
              </div>
            ) : (
              <>
                {error ? <ErrorBanner error={error} /> : null}
                <div>
                  <label className={styles.label} htmlFor="portal-forgot-email">
                    E-Mail
                  </label>
                  <div className={styles.control}>
                    <input
                      id="portal-forgot-email"
                      className={styles.input}
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      autoComplete="email"
                      autoFocus
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  className={`pressable ${styles.primary}`}
                  disabled={pending || !email.trim()}
                >
                  Link anfordern
                </button>
              </>
            )}
            <button
              type="button"
              className={clsx(styles.link, local.centerLink)}
              onClick={() => {
                setMode('login');
                setError(null);
              }}
            >
              Zurück zur Anmeldung
            </button>
          </div>
        </form>
      </PhoneFrame>
    );
  }

  return (
    <PhoneFrame>
      <form className={local.form} onSubmit={(event) => void submit(event)} noValidate>
        <div className={local.head}>
          <div className={styles.brand}>DAMOKLES</div>
          {error ? null : <div className={styles.brandSub}>Mandantenportal</div>}
          <h1 className={clsx(styles.title, error ? local.headTitleError : local.headTitle)}>
            Willkommen zurück
          </h1>
        </div>
        {error ? (
          <div className={local.bannerSlot}>
            <ErrorBanner error={error} />
          </div>
        ) : null}
        <div className={`${styles.fields} ${error ? local.fieldsAfterBanner : local.fields}`}>
          <div>
            <label className={styles.label} htmlFor="portal-login-email">
              E-Mail
            </label>
            <div className={styles.control}>
              <input
                id="portal-login-email"
                className={styles.input}
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="username"
                required
              />
            </div>
          </div>
          <div>
            <label className={styles.label} htmlFor="portal-login-password">
              Passwort
            </label>
            <PasswordInput
              id="portal-login-password"
              value={password}
              onChange={setPassword}
              invalid={error !== null}
              autoComplete="current-password"
              toggle={false}
            />
          </div>
          {error ? null : (
            <div className={local.optionsRow}>
              <label className={local.remember}>
                <input
                  type="checkbox"
                  className={local.rememberInput}
                  checked={remember}
                  onChange={(event) => setRemember(event.target.checked)}
                />
                <span className={local.rememberBox} aria-hidden="true">
                  <Check size={11} strokeWidth={3} />
                </span>
                Angemeldet bleiben
              </label>
              <button type="button" className={styles.link} onClick={() => setMode('forgot')}>
                Passwort vergessen
              </button>
            </div>
          )}
          <button
            type="submit"
            className={clsx('pressable', styles.primary, !error && local.submit)}
            disabled={pending}
          >
            Anmelden
          </button>
          {error ? (
            <button
              type="button"
              className={clsx(styles.link, local.centerLink)}
              onClick={() => {
                setMode('forgot');
                setError(null);
              }}
            >
              Passwort zurücksetzen
            </button>
          ) : null}
        </div>
        {footer}
      </form>
    </PhoneFrame>
  );
}

function ErrorBanner({ error }: { error: LoginError }) {
  return (
    <div className={styles.banner} role="alert">
      <TriangleAlert size={17} strokeWidth={1.75} aria-hidden="true" />
      <div>
        <div className={styles.bannerTitle}>{error.title}</div>
        {error.warning ? <div className={styles.bannerText}>{error.warning}</div> : null}
      </div>
    </div>
  );
}
